import { api } from '@/api'
import { useSpreadInfoStore } from '@/stores'

export interface FbEventConfig {
  enabled: boolean
  reportMode?: string | null
  targetType?: string | null
  targetValue?: number | null
  thresholdMinutes?: number | null
}

export interface FbRuntimeConfig {
  userBehaviorEnabled: boolean
  channelMask: number
  pixelId: string
  events: Record<string, FbEventConfig>
}

declare function fbq(...args: any[]): void

// 模块加载时记录：HTML 入口是否已用 pixelId 初始化过 SDK（即 localStorage 里已有值）
const sdkPreInitialized = !!localStorage.getItem('fb_pixel_code')

function initFbPixel(pixelId: string) {
  localStorage.setItem('fb_pixel_code', pixelId)
  if (typeof fbq === 'undefined')
    return
  fbq('init', pixelId)
  // 仅当 HTML 入口未初始化（首次 FB 落地但 URL 无 pixel_code）时才补发 PageView
  if (!sdkPreInitialized)
    fbq('track', 'PageView')
}

export function waitForFbConfig(timeoutMs = 5000): Promise<void> {
  const store = useSpreadInfoStore()
  if (store.fbRuntimeConfig)
    return Promise.resolve()

  return new Promise((resolve) => {
    const interval = 50
    let elapsed = 0
    const timer = setInterval(() => {
      elapsed += interval
      if (store.fbRuntimeConfig || elapsed >= timeoutMs) {
        clearInterval(timer)
        resolve()
      }
    }, interval)
  })
}

export async function fetchFbRuntimeConfig(pId: string) {
  const store = useSpreadInfoStore()
  if (store.fbRuntimeConfig)
    return
  try {
    const { data } = await api.get<FbRuntimeConfig>('/tracking/runtime-config', {
      params: { pId, platform: '3' },
    })
    store.$setFbRuntimeConfig(data)
    if (data.pixelId)
      initFbPixel(data.pixelId)
  }
  catch (e) {
    console.warn('fetchFbRuntimeConfig failed', e)
  }
}

export function useFbRuntimeConfig() {
  const store = useSpreadInfoStore()

  function isEventEnabled(eventKey: string): boolean {
    const config = store.fbRuntimeConfig
    if (!config || !config.userBehaviorEnabled)
      return false
    // channelMask & 1 !== 0 表示覆盖 H5 平台
    if ((config.channelMask & 1) === 0)
      return false
    return config.events?.[eventKey]?.enabled === true
  }

  function getEventConfig(eventKey: string) {
    return store.fbRuntimeConfig?.events?.[eventKey]
  }

  return { isEventEnabled, getEventConfig }
}
